/**
 * Indigo DatePicker component for the design system.
 * A customizable date picker built on top of Ant Design DatePicker.
 *
 * Features:
 * - Integration with design system
 * - Error state handling
 * - Disabled state with text display
 * - Custom formatting options
 * - Accessibility support
 */
import { DatePicker as AntDatePicker } from "antd";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import { clsx } from "clsx";
import dayjs, { Dayjs } from "dayjs";
import { FC, useCallback, useEffect, useState } from "react";
import { hasActiveTextSelection } from "~/utils/textInputUtils";
import { DateISO } from "../../utils/DateISO";
import { LabelBlock } from "../LabelBlock";
import { Tooltip } from "../Tooltip";
import "./DatePicker.css";

/**
 * Props for DatePicker component
 */
export interface DatePickerProps {
  /**
   * Visual style variant of the date picker.
   * @default "filled"
   */
  variant?: "outline" | "filled";

  /**
   * Current date value in ISO format (as string or DateISO instance)
   */
  value?: string | DateISO | null;

  /**
   * Callback fired when date changes
   */
  onChange?: (value: string | null) => void;

  /**
   * Whether the datepicker is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the datepicker should use full width (100%) instead of default fixed width
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Whether the datepicker is in an error state
   * @default false
   */
  isError?: boolean;

  /**
   * Callback fired when datepicker gains focus (useful for clearing error states)
   */
  onFocus?: () => void;

  /**
   * Label text to display above the datepicker
   */
  label?: string;

  /**
   * Description text to display with the datepicker
   */
  description?: string;

  /**
   * Width of the datepicker container as percentage
   * @default "100%"
   */
  widthPercent?: string;

  /**
   * Formats accepted by the date picker
   * @default ["MM/DD/YYYY", "M/D/YYYY"]
   */
  formats?: string[];

  /**
   * Format to display the date
   * @default "MM/DD/YYYY"
   */
  displayFormat?: string;

  /**
   * Placeholder text when no date is selected
   * @default "Select date"
   */
  placeholder?: string;

  /**
   * Test ID for the date picker element
   */
  dateTestId?: string;

  /**
   * Test ID for the date picker popup
   */
  datePopupTestId?: string;

  /**
   * Additional CSS class for the component
   */
  className?: string;

  /**
   * Size of the control
   * - "small": 24px height
   * - "default": 32px height
   * - "large": 40px height
   * @default "default"
   */
  size?: "small" | "default" | "large";

  /**
   * Tooltip text shown on hover of a "?" icon next to the label (edit mode only).
   */
  infoTooltip?: string;

  /** Show the label tooltip in display mode as well as edit mode. */
  alwaysShowInfoTooltip?: boolean;
}

/**
 * DatePicker Component
 *
 * A design system date picker that provides date selection capabilities
 * with full styling integration and state management.
 *
 * @example
 * // Basic usage
 * <DatePicker
 *   value={dateValue}
 *   onChange={setDateValue}
 *   label="Select Date"
 * />
 *
 * @example
 * // With error state
 * <DatePicker
 *   value={dateValue}
 *   onChange={setDateValue}
 *   isError={!!validationError}
 *   onFocus={() => setValidationError(null)}
 *   label="Appointment Date"
 *   description="Please select a future date"
 * />
 */
export const DatePicker: FC<DatePickerProps> = ({
  variant = "filled",
  value,
  onChange,
  disabled = false,
  fullWidth = false,
  isError = false,
  onFocus,
  label,
  description,
  widthPercent = "auto",
  formats = ["MM/DD/YYYY", "M/D/YYYY"],
  displayFormat = "MM/DD/YYYY",
  placeholder = "Select date",
  dateTestId,
  datePopupTestId,
  className = "",
  size = "default",
  infoTooltip,
  alwaysShowInfoTooltip = false,
}) => {
  const [date, setDate] = useState<Dayjs | null>(null);
  const [formattedDate, setFormattedDate] = useState<string | null>(null);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const antSize: SizeType =
    size === "small" ? "small" : size === "large" ? "large" : "middle";
  const popupClassNames = clsx(
    "indigo-datepicker-dropdown",
    fullWidth && "indigo-datepicker-dropdown--full-width",
    datePopupTestId,
  );

  // Handler for date changes
  const handleDateChange = useCallback(
    (newDate: Dayjs | null) => {
      onChange?.(newDate ? newDate.format("YYYY-MM-DD") : null);
    },
    [onChange],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    if (value) {
      const newDate = dayjs(value.toString());
      setDate(newDate);
      setFormattedDate(newDate.format(displayFormat));
    } else {
      setDate(null);
      setFormattedDate(null);
    }
  }, [value, displayFormat, setDate, setFormattedDate]);

  // If disabled, display text only
  if (disabled) {
    const handleCopyOnClick = () => {
      if (hasActiveTextSelection()) {
        return;
      }

      if (formattedDate) {
        navigator.clipboard
          .writeText(formattedDate)
          .then(() => {
            setShowCopiedTooltip(true);
            setTimeout(() => setShowCopiedTooltip(false), 2000);
          })
          .catch(() => {
            // Silently fail if clipboard access is denied
          });
      }
    };

    return (
      <LabelBlock
        label={label}
        description={description}
        disabled={true}
        widthPercent={widthPercent}
        infoTooltip={infoTooltip}
        alwaysShowInfoTooltip={alwaysShowInfoTooltip}
      >
        <Tooltip
          content="Copied!"
          placement="top"
          size="sm"
          visible={showCopiedTooltip}
        >
          <div
            className="indigo-datepicker-disabled-text"
            onClick={handleCopyOnClick}
          >
            {formattedDate || "-"}
          </div>
        </Tooltip>
      </LabelBlock>
    );
  }

  // Render the interactive date picker
  return (
    <LabelBlock
      label={label}
      description={description}
      isError={isError}
      disabled={disabled}
      widthPercent={widthPercent}
      infoTooltip={infoTooltip}
      alwaysShowInfoTooltip={alwaysShowInfoTooltip}
    >
      <div
        className={clsx(
          "indigo-datepicker",
          // variant styles
          {
            "indigo-datepicker--filled": variant === "filled",
            "indigo-datepicker--full-width": fullWidth,
          },
          `indigo-datepicker--${size}`,
          className,
        )}
      >
        <AntDatePicker
          size={antSize}
          value={date}
          onChange={handleDateChange}
          onFocus={onFocus}
          className={date ? "has-value" : "no-value"}
          placeholder={placeholder}
          format={formats}
          status={isError ? "error" : ""}
          data-testid={dateTestId}
          getPopupContainer={
            fullWidth
              ? (trigger) => trigger.parentNode as HTMLElement
              : undefined
          }
          style={{ width: fullWidth ? "100%" : undefined }}
          classNames={{
            popup: {
              root: popupClassNames,
            },
          }}
        />
      </div>
    </LabelBlock>
  );
};

export default DatePicker;
